import { Link } from "react-router";

import { Chip, ChipRow } from "../lib/Chip.tsx";
import { EmptyState } from "../lib/EmptyState.tsx";
import { OndoCode } from "../lib/OndoCode.tsx";
import { WorkThumb } from "../lib/WorkThumb.tsx";
import { CATALOGUE_COPY } from "./estate.constants.ts";
import { CATALOGUE_CHIPS } from "./catalogue.model.ts";
import { useCatalogueController } from "./useCatalogueController.ts";
import "./estate.css";

/**
 * The catalogue raisonné — the estate portal's front page.
 *
 * Renders what `ondo.estate_catalogue()` may say and nothing more: no owner,
 * no placement, no sale colour. The badge on a row is the estate's OWN
 * confirmation of the AUTHORSHIP claim, which is the one truth-adjacent claim
 * this screen can make without the registry concluding anything.
 *
 * MOST OF THIS LIST IS NOT THE CATALOGUE, and OND-20 is why it now says so on
 * every row. The read returns every work anybody has NAMED this artist on, and
 * a catalogue number is issued by a confirmed authorship claim and by nothing
 * else — so each row carries what any authority has said beside what this
 * estate has said, and an unnumbered row says it is unnumbered rather than
 * leaving a gap where a number would be.
 */
export function Catalogue({ workBase, canDeclare }: { workBase: string; canDeclare: boolean }) {
  const {
    view,
    thumbs,
    query,
    setQuery,
    chip,
    setChip,
    declare,
    declaring,
    declareArmed,
    disarmDeclare,
  } = useCatalogueController({ workBase, canDeclare });

  if (view.kind === "loading") {
    return (
      <p className="estate__note" role="status">
        {view.message}
      </p>
    );
  }

  if (view.kind === "error") {
    return (
      <section className="estate__note estate__note--error" role="alert">
        <h2 className="estate__note-title">{view.title}</h2>
        <p>{view.message}</p>
      </section>
    );
  }

  if (view.kind === "empty") {
    return (
      <EmptyState className="estate__empty" lead={view.lead} detail={view.detail} />
    );
  }

  return (
    <>
      <p className="estate__summary">{view.header}</p>

      {view.banner.state === "declared" ? (
        <section className="estate__banner estate__banner--declared">
          <p>{view.banner.line}</p>
        </section>
      ) : (
        <section className="estate__banner">
          <div>
            <p className="estate__banner-lead">{CATALOGUE_COPY.inProgressLead}</p>
            <p className="estate__banner-detail">{CATALOGUE_COPY.inProgressDetail}</p>
          </div>
          {view.banner.state === "in-progress" && view.banner.declare && (
            <DeclareControls
              armed={declareArmed}
              declare={view.banner.declare}
              declaring={declaring}
              onDeclare={(artistId) => void declare(artistId)}
              onDisarm={disarmDeclare}
            />
          )}
        </section>
      )}

      <div className="estate__toolbar">
        <input
          className="estate__search"
          onChange={(event) => setQuery(event.target.value)}
          placeholder={CATALOGUE_COPY.searchPlaceholder}
          type="search"
          value={query}
        />
        <span className="estate__showing">{view.showing}</span>
      </div>

      <ChipRow label={CATALOGUE_COPY.chipRowLabel}>
        {CATALOGUE_CHIPS.map((candidate) => (
          <Chip
            key={candidate}
            onClick={() => setChip(candidate)}
            pressed={chip === candidate}
          >
            {CATALOGUE_COPY.chipLabels[candidate]} · {view.counts[candidate]}
          </Chip>
        ))}
      </ChipRow>

      {view.rows.length === 0 ? (
        <p className="estate__note">{CATALOGUE_COPY.noMatches}</p>
      ) : (
        <ul className="estate__rows">
          {view.rows.map((row) => (
            <li className="estate__row" key={row.workId}>
              <span className="estate__thumb">
                <WorkThumb imageUrl={thumbs.get(row.workId) ?? null} title={row.title} />
              </span>
              <span className="estate__row-body">
                <Link className="estate__row-title" to={row.href}>
                  {row.title}
                </Link>
                <span className="estate__row-meta">{row.meta}</span>
                {/* WHAT ANY AUTHORITY HAS SAID, on every row. Beside the meta
                    rather than in the badge column, because it is a fact about
                    the work and the badge over there is about this estate — and
                    a reader who could not tell those apart would read "not
                    confirmed by us" as "not confirmed". */}
                <span className="estate__row-attribution">{row.attribution}</span>
                {/* WHO OWNS IT — OND-58, and it sits on every row in both
                    states. The model already chose between the name and the
                    withheld sentence, so there is no condition here: a screen
                    that decided which to draw would be a second place the
                    hide-or-show rule lived, and the work record is the other
                    one. */}
                <span className="estate__row-owner">
                  <span className="estate__row-owner-label">
                    {CATALOGUE_COPY.ownerLabel}
                  </span>
                  {row.owner}
                </span>
                {row.recordOpen && (
                  <span className="estate__row-note">{CATALOGUE_COPY.recordOpen}</span>
                )}
                {row.crNote !== null && (
                  <span className="estate__row-note">{row.crNote}</span>
                )}
              </span>
              {/* UNDISCLOSED ON EVERY ROW, and that is the decision rather than
                  missing data. 20260911090000 decision 4 refuses this screen a
                  sale colour — most of these works belong to somebody else and
                  their sale state is that owner's business — so `sold` is null
                  and band 1 draws as hairline outlines. The refusal now looks
                  like a refusal instead of looking like green. */}
              <OndoCode
                code={row.ondoCode}
                facts={{ sold: null, authorityAttested: null }}
              />
              <span className="estate__row-end">
                {/* Dimmed where no number was issued, so a list that is mostly
                    unnumbered does not read as a list of numbers with gaps. */}
                <span
                  className={`estate__cr${row.confirmed ? "" : " estate__cr--unconfirmed"}`}
                >
                  {row.cr}
                </span>
                <span
                  className={`estate__badge${row.attested ? " estate__badge--attested" : ""}`}
                >
                  {row.attested ? CATALOGUE_COPY.attested : CATALOGUE_COPY.unattested}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

/** The two-press declare, its target already narrowed by the caller. */
function DeclareControls({
  declare,
  armed,
  declaring,
  onDeclare,
  onDisarm,
}: {
  declare: { artistId: string; label: string };
  armed: boolean;
  declaring: boolean;
  onDeclare: (artistId: string) => void;
  onDisarm: () => void;
}) {
  return (
    <span className="estate__banner-actions">
      {armed && (
        <button className="estate__ghost" onClick={onDisarm} type="button">
          {CATALOGUE_COPY.declareKeep}
        </button>
      )}
      <button
        className="estate__declare"
        disabled={declaring}
        onClick={() => onDeclare(declare.artistId)}
        type="button"
      >
        {armed ? CATALOGUE_COPY.declareConfirm : declare.label}
      </button>
    </span>
  );
}
